import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  TrendingUp, 
  Receipt, 
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Menu,
  LogOut,
  User,
  Shield,
  Eye,
  MapPin,
  MessageCircle,
  Settings,
  Search,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/properties", icon: Building2, label: "Properties" },
  { path: "/property-analyser", icon: Search, label: "Property Analyser" },
  { path: "/cashflow", icon: Receipt, label: "Cashflow" },
  { path: "/forecasting", icon: TrendingUp, label: "Forecasting" },
  { path: "/recommendations", icon: Lightbulb, label: "Recommendations" },
  { path: "/suburb-research", icon: MapPin, label: "Suburb Research" },
  { path: "/settings", icon: Settings, label: "Settings" },
  { path: "https://www.investepsproperty.com/contact", icon: MessageCircle, label: "Speak to an Agent", external: true },
];

export const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [branding, setBranding] = useState({
    app_name: "The Portfolio Accelerator",
    logo_url: null,
    has_custom_branding: false,
    brand_color: null,
    hide_speak_to_agent: false
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();

  // Get the effective brand color (partner's color or default)
  const brandColor = branding.brand_color || "#547d91";
  const brandColorHover = branding.brand_color 
    ? adjustColor(branding.brand_color, -15) 
    : "#456a7a";

  // Helper function to darken a hex color
  function adjustColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, Math.min(255, (num >> 16) + amt));
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  }

  // Helper function to convert hex to RGB values
  function hexToRgb(hex) {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `${r}, ${g}, ${b}`;
  }

  useEffect(() => {
    // Fetch branding for current user
    const fetchBranding = async () => {
      try {
        const response = await api.get("/branding");
        setBranding(response.data);
      } catch (error) {
        console.error("Error fetching branding:", error);
      }
    };
    fetchBranding();
  }, [user]);

  // Update document title and favicon when branding changes
  useEffect(() => {
    // Update page title
    document.title = branding.app_name || "The Portfolio Accelerator";
    
    // Update favicon if partner has a custom logo
    if (branding.logo_url) {
      const faviconLink = document.querySelector("link[rel='icon']");
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
      const fullLogoUrl = `${API_URL}${branding.logo_url}`;
      
      if (faviconLink) {
        faviconLink.href = fullLogoUrl;
      }
      if (appleTouchIcon) {
        appleTouchIcon.href = fullLogoUrl;
      }
    }
    
    // Set CSS variables for brand colors globally
    const root = document.documentElement;
    const color = branding.brand_color || "#547d91";
    const hoverColor = branding.brand_color ? adjustColor(branding.brand_color, -15) : "#456a7a";
    const lightColor = branding.brand_color ? adjustColor(branding.brand_color, 40) : "#e8f0f3";
    
    root.style.setProperty('--brand-color', color);
    root.style.setProperty('--brand-color-hover', hoverColor);
    root.style.setProperty('--brand-color-light', lightColor);
    root.style.setProperty('--brand-color-rgb', hexToRgb(color));
  }, [branding]);

  useEffect(() => {
    // Check if user is admin, partner, and impersonation status
    const checkStatus = async () => {
      try {
        const [adminRes, partnerRes, impersonateRes] = await Promise.all([
          api.get("/admin/check"),
          api.get("/partner/check"),
          api.get("/admin/impersonation-status")
        ]);
        setIsAdmin(adminRes.data.is_admin);
        setIsPartner(partnerRes.data.is_partner);
        setIsImpersonating(impersonateRes.data.is_impersonating);
        if (impersonateRes.data.is_impersonating) {
          setImpersonatedUser(impersonateRes.data.impersonated_user);
        }
      } catch (error) {
        setIsAdmin(false);
        setIsPartner(false);
        setIsImpersonating(false);
      }
    };
    if (user) {
      checkStatus();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleStopImpersonation = async () => {
    try {
      await api.post("/admin/stop-impersonation");
      // Refresh auth state and redirect to admin
      await checkAuth();
      navigate("/admin");
      window.location.reload();
    } catch (error) {
      console.error("Error stopping impersonation:", error);
    }
  };

  // Build dynamic nav items
  let dynamicNavItems = [...navItems];
  
  // Hide "Speak to an Agent" if partner has it disabled
  if (branding.hide_speak_to_agent) {
    dynamicNavItems = dynamicNavItems.filter(item => item.label !== "Speak to an Agent");
  }
  
  // Add Partner Dashboard if user is a partner
  if (isPartner) {
    const settingsIndex = dynamicNavItems.findIndex(item => item.path === "/settings");
    dynamicNavItems.splice(settingsIndex, 0, {
      path: "/partner-dashboard",
      icon: Users,
      label: "Partner Dashboard"
    });
  }

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      {/* Impersonation Banner */}
      {isImpersonating && impersonatedUser && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">
              Viewing as: <strong>{impersonatedUser.name}</strong> ({impersonatedUser.email})
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleStopImpersonation}
            className="bg-white text-amber-600 hover:bg-amber-50"
            data-testid="stop-impersonation-btn"
          >
            Return to Admin
          </Button>
        </div>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-slate-200 transition-all duration-300",
        collapsed ? "w-20" : "w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        isImpersonating ? "top-10" : "top-0"
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-100 flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2">
              {branding.logo_url ? (
                <img 
                  src={`${API_URL}${branding.logo_url}`}
                  alt={branding.app_name} 
                  className="w-8 h-8 rounded-lg object-contain"
                />
              ) : (
                <img 
                  src="/investeps-logo.png" 
                  alt="Investeps Logo" 
                  className="w-8 h-8 rounded-lg object-contain"
                />
              )}
              <span className="font-heading text-lg font-semibold text-slate-800">{branding.app_name}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex text-slate-500 hover:text-slate-700"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto min-h-0">
          {dynamicNavItems.map((item) => {
            const isActive = !item.external && (location.pathname === item.path || 
              (item.path !== "/" && location.pathname.startsWith(item.path)));
            
            // External link
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors",
                    "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className={cn("w-5 h-5 flex-shrink-0", collapsed && "mx-auto")} />
                  {!collapsed && <span>{item.label}</span>}
                </a>
              );
            }
            
            // Internal link
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors",
                  isActive 
                    ? "text-white" 
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
                style={isActive ? { backgroundColor: brandColor } : {}}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className={cn("w-5 h-5 flex-shrink-0", collapsed && "mx-auto")} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer - always at bottom */}
        {!collapsed && (
          <div className="p-4 border-t border-slate-100 flex-shrink-0">
            <p className="text-xs text-slate-400 text-center">
              {branding.app_name}
            </p>
            <p className="text-xs text-slate-400 text-center mt-1">
              Powered by{" "}
              <a 
                href="https://www.investepsproperty.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: brandColor }}
              >
                Investeps Property
              </a>
            </p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden mr-2"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-xl font-semibold text-slate-800">
              {navItems.find(item => 
                item.path === location.pathname || 
                (item.path !== "/" && location.pathname.startsWith(item.path))
              )?.label || "Dashboard"}
            </h1>
          </div>
          
          {/* User Menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#547d91] flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium text-slate-700">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-xs text-slate-500">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => navigate("/admin")} 
                      className="cursor-pointer"
                      data-testid="admin-link"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
